// Bezoeken (/(tabs)/bezoeken) — web-port van app/(tabs)/bezoeken.tsx met fictieve demo-visits.
import React, { useEffect, useState } from 'react';
import Icon from '../../Icon';
import { C } from '../../theme';
import { PROPS, demoBezoekenState } from '../data';

const sh = { card: '0 6px 16px rgba(15,27,45,0.08)', soft: '0 2px 8px rgba(15,27,45,0.05)' };

const PHASES = [
  { key: 'nog_in_te_plannen', short: 'In te plannen', icon: 'edit-3' },
  { key: 'wachten_op_partner', short: 'Wacht partner', icon: 'users' },
  { key: 'moment_gevonden', short: 'Moment', icon: 'target' },
  { key: 'bezoek_aangevraagd', short: 'Aangevraagd', icon: 'send' },
  { key: 'bezoek_bevestigd', short: 'Bevestigd', icon: 'check-circle' },
  { key: 'bezoek_voltooid', short: 'Voltooid', icon: 'check' },
  { key: 'nog_te_beoordelen', short: 'Beoordelen', icon: 'star' },
];

function BellButton() {
  return (
    <div style={{
      width: 38, height: 38, borderRadius: 19, background: C.white,
      border: `1px solid ${C.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
      position: 'relative', boxShadow: sh.soft,
    }}>
      <Icon name="bell" size={16} color={C.navy} />
      <span style={{ position: 'absolute', top: 8, right: 9, width: 7, height: 7, borderRadius: 4, background: C.gold, border: `1.5px solid ${C.white}` }} />
    </div>
  );
}

function HubCard({ icon, title, desc, count, accent, subtle, onClick, testid }) {
  return (
    <button
      className="immo-btn"
      data-testid={testid}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
        background: accent ? C.navy : subtle ? C.sand : C.white,
        borderRadius: 20, padding: 12,
        border: `1px solid ${accent ? 'rgba(196,138,58,0.55)' : C.line}`,
        boxShadow: sh.soft, boxSizing: 'border-box',
      }}
    >
      <div style={{
        width: 44, height: 44, borderRadius: 22, flexShrink: 0,
        background: accent ? C.gold : 'rgba(196,138,58,0.14)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon name={icon} size={20} color={accent ? C.navy : C.gold} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1, color: accent ? C.white : C.navy, fontSize: 15, fontWeight: 600 }}>{title}</span>
          <span style={{
            minWidth: 26, padding: '3px 8px', borderRadius: 999, textAlign: 'center',
            background: accent ? C.gold : 'rgba(16,42,67,0.08)',
            color: C.navy, fontSize: 12, fontWeight: 800,
          }}>{count}</span>
        </div>
        <div style={{ color: accent ? 'rgba(255,255,255,0.75)' : C.inkMuted, fontSize: 12, lineHeight: '17px', marginTop: 3 }}>{desc}</div>
      </div>
      <Icon name="chevron-right" size={18} color={accent ? 'rgba(255,255,255,0.6)' : C.inkFaint} />
    </button>
  );
}

function PhaseChip({ label, icon, count, active, onClick }) {
  return (
    <button
      className="immo-btn"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 5, background: active ? C.navy : C.white,
        padding: '6px 10px', borderRadius: 999, border: `1px solid ${active ? C.navy : C.line}`, flexShrink: 0,
      }}
    >
      {icon ? <Icon name={icon} size={11} color={active ? C.white : C.navy} /> : null}
      <span style={{ color: active ? C.white : C.navy, fontSize: 11, fontWeight: 700, letterSpacing: 0.4 }}>{label}</span>
      {typeof count === 'number' ? (
        <span style={{ color: active ? C.gold : C.inkFaint, fontSize: 10, fontWeight: 800 }}>{count}</span>
      ) : null}
    </button>
  );
}

function VisitCard({ visit, p, whenLabel }) {
  const confirmed = visit.status === 'ingepland';
  return (
    <div style={{ background: C.white, borderRadius: 14, padding: 14, border: `1px solid ${C.line}`, display: 'flex', flexDirection: 'column', gap: 12, boxShadow: sh.soft }} data-testid={`visit-${visit.id}`}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
        <img src={p.img} alt={p.title} style={{ width: 88, height: 88, borderRadius: 14, objectFit: 'cover', background: C.line }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ color: C.navy, fontSize: 15, fontWeight: 600 }}>{p.title}</div>
          <div style={{ color: C.inkMuted, fontSize: 13 }}>{p.city} · {p.region}</div>
          <span style={{
            alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '4px 9px', borderRadius: 999, marginTop: 2,
            background: confirmed ? C.greenSoft : C.goldSoft, color: C.navy,
          }}>
            <Icon name={confirmed ? 'calendar' : 'clock'} size={11} color={C.navy} />
            <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {confirmed ? 'Ingepland' : 'Aangevraagd'}
            </span>
          </span>
        </div>
      </div>

      {!confirmed ? (
        <>
          <div style={{ color: C.inkMuted, fontSize: 13, fontStyle: 'italic', padding: '0 4px' }}>
            {visit.is_couple
              ? 'Deel wanneer je kan. Je partner doet hetzelfde en we tonen automatisch een gemeenschappelijk moment.'
              : 'Kies wanneer jij kan langsgaan.'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '12px 0', borderRadius: 999, background: C.navy }}>
            <Icon name="calendar" size={14} color={C.white} />
            <span style={{ color: C.white, fontSize: 14, fontWeight: 600 }}>{visit.is_couple ? 'Deel je beschikbaarheid' : 'Kies een moment'}</span>
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: C.greenSoft, padding: 10, borderRadius: 14, border: `1px solid ${C.green}` }}>
            <Icon name="check-circle" size={16} color={C.green} />
            <span style={{ flex: 1, color: C.navy, fontSize: 14, fontWeight: 600, textTransform: 'capitalize' }}>{whenLabel}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: C.greenSoft, padding: '8px 12px', borderRadius: 14 }}>
            <Icon name="check-circle" size={12} color={C.green} />
            <span style={{ color: C.navy, fontSize: 12, fontWeight: 600 }}>Bezoek bevestigd door makelaar</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 0', borderRadius: 999, background: C.white, border: `1px solid ${C.line}` }}>
              <Icon name="external-link" size={14} color={C.navy} />
              <span style={{ color: C.navy, fontSize: 13, fontWeight: 700 }}>Google Agenda</span>
            </div>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 0', borderRadius: 999, background: C.navy }}>
              <Icon name="external-link" size={14} color={C.white} />
              <span style={{ color: C.white, fontSize: 13, fontWeight: 700 }}>Outlook</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 0', borderRadius: 999, background: C.greenSoft, border: `1px solid ${C.green}` }}>
              <Icon name="check" size={14} color={C.green} />
              <span style={{ color: C.navy, fontSize: 13, fontWeight: 700 }}>Bezoek voltooid</span>
            </div>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 0', borderRadius: 999, background: C.white, border: `1px solid ${C.line}` }}>
              <span style={{ color: C.navy, fontSize: 13, fontWeight: 600 }}>Verplaats</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function BezoekenApp({ mode, step }) {
  const isCouple = mode === 'couple';
  const state = demoBezoekenState(mode, step?.id ?? -1);
  const [view, setView] = useState(state.view);

  // De Ghost Driver stuurt de fase aan op basis van de scriptstap.
  useEffect(() => { setView(state.view); }, [state.view]);

  const p = PROPS[state.visit.property_id];

  return (
    <div data-testid="bezoeken-screen" style={{ minHeight: '100%', background: C.bgSoft, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '46px 24px 8px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ color: C.navy, fontSize: 24, fontWeight: 700, letterSpacing: -0.3 }}>Geplande bezoeken</div>
          <div style={{ color: C.inkMuted, fontSize: 13 }}>
            {isCouple
              ? 'Gekoppeld — Woningmatches actief wanneer beide partners instemmen.'
              : 'Duid je beschikbaarheden aan en plan je bezoeken.'}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <BellButton />
          <span style={{ color: C.inkFaint, marginTop: 3, fontSize: 8.5, letterSpacing: 1.1, fontWeight: 700 }}>MELDINGEN</span>
        </div>
      </div>

      {view === 'hub' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 16px 80px' }}>
          <div style={{ color: C.inkMuted, fontSize: 12.5, marginBottom: 4 }}>Waar wil je aan werken?</div>
          <HubCard
            icon="clock"
            title="Nog in te plannen"
            desc={isCouple ? 'Woningen die jullie leuk vonden — kies samen een moment.' : 'Woningen die je naar rechts swipete — plan er een bezoek voor.'}
            count={state.counts.nog_in_te_plannen}
            accent
            onClick={() => setView('alle')}
            testid="hub-in-te-plannen"
          />
          <HubCard
            icon="calendar"
            title="Gepland"
            desc="Bezoeken met een vast moment. Voeg toe aan agenda."
            count={state.counts.gepland}
            onClick={() => setView('alle')}
            testid="hub-gepland"
          />
          <HubCard
            icon="check-circle"
            title="Bezocht"
            desc="Woningen waar je bent langs geweest — beoordeel en vergelijk."
            count={0}
            onClick={() => setView('alle')}
            testid="hub-bezocht"
          />
          {state.maybeCount > 0 ? (
            <HubCard
              icon="radio"
              title="Op de radar"
              desc={isCouple ? "Woningen die jullie als 'misschien' hebben gemarkeerd." : "Woningen die je als 'misschien' hebt gemarkeerd."}
              count={state.maybeCount}
              subtle
              onClick={() => setView('alle')}
              testid="hub-op-de-radar"
            />
          ) : null}
          <HubCard
            icon="list"
            title="Alle bezoeken"
            desc="Zie de volledige lijst in één scroll."
            count={state.counts.alle}
            subtle
            onClick={() => setView('alle')}
            testid="hub-alle"
          />
        </div>
      ) : (
        <>
          {/* Fase-filter pills */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '4px 16px', overflowX: 'auto' }}>
            <button
              className="immo-btn"
              data-testid="back-to-hub"
              onClick={() => setView('hub')}
              style={{
                display: 'flex', alignItems: 'center', gap: 3, padding: '6px 10px', borderRadius: 999,
                background: 'rgba(196,138,58,0.12)', border: '1px solid rgba(196,138,58,0.30)', flexShrink: 0,
              }}
            >
              <Icon name="chevron-left" size={14} color={C.navy} />
              <span style={{ color: C.navy, fontSize: 11.5, fontWeight: 800 }}>Overzicht</span>
            </button>
            <PhaseChip label="Alle" count={state.counts.alle} active onClick={() => {}} />
            {PHASES.filter((ph) => isCouple || ph.key !== 'wachten_op_partner').map((ph) => (
              <PhaseChip key={ph.key} label={ph.short} icon={ph.icon} count={ph.key === 'bezoek_bevestigd' && state.visit.status === 'ingepland' ? 1 : 0} active={false} onClick={() => {}} />
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 24px 80px' }}>
            <VisitCard visit={state.visit} p={p} whenLabel={state.whenLabel} />
          </div>
        </>
      )}
    </div>
  );
}
